import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { TestTube, Upload, Trash2, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MedicalTests = () => {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [testName, setTestName] = useState('');
  const [description, setDescription] = useState('');
  const [testDate, setTestDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => { fetchTests(); }, []);
  const fetchTests = async () => {
    try { const res = await axios.get(`${API}/medical-tests/${user.id}`, { headers: { Authorization: `Bearer ${token}` } }); setTests(res.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!testName.trim()) { toast.error(t.testName); e.target.value = ''; return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file); formData.append('patient_id', user.id); formData.append('test_name', testName); formData.append('description', description); formData.append('test_date', testDate);
      await axios.post(`${API}/medical-tests/upload`, formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      toast.success(t.upload + ' OK!'); setTestName(''); setDescription(''); fetchTests();
    } catch (err) { toast.error('Failed'); } finally { setUploading(false); e.target.value = ''; }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await axios.delete(`${API}/medical-tests/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('OK'); setTests(tests.filter(t => t.id !== id)); }
    catch (e) { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F4C5C]"></div></div>;

  return (
    <div className="p-6 lg:p-8" data-testid="medical-tests-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{t.medicalTests}</h1>
        <p className="text-[#6B7280]">{t.medicalTestsSubtitle}</p>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-6">
        <h2 className="text-lg font-bold text-[#1F2937] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t.uploadNewTest}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <input type="text" value={testName} onChange={e => setTestName(e.target.value)} placeholder={t.testName + ' *'} className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20" data-testid="test-name-input" />
          <input type="date" value={testDate} onChange={e => setTestDate(e.target.value)} className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20" data-testid="test-date-input" />
          <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder={t.descriptionOptional} className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20" data-testid="test-description" />
          <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-[#0F4C5C]/30 rounded-lg cursor-pointer hover:bg-[#0F4C5C]/5 transition-colors" data-testid="test-upload-btn">
            <Upload className="w-5 h-5 text-[#0F4C5C]" /><span className="text-sm font-medium text-[#0F4C5C]">{uploading ? t.uploading : t.clickToUploadTest}</span>
            <input type="file" accept="image/*,.pdf" onChange={handleUpload} className="hidden" disabled={uploading} />
          </label>
        </div>
      </div>

      {tests.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center"><TestTube className="w-12 h-12 text-[#6B7280] mx-auto mb-4" /><p className="text-[#6B7280]">{t.noTestsYet}</p></div>
      ) : (
        <div className="space-y-3">
          {tests.map(test => (
            <div key={test.id} className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 flex items-center justify-between" data-testid={`test-card-${test.id}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#0F4C5C]/10 rounded-lg flex items-center justify-center shrink-0"><FileText className="w-5 h-5 text-[#0F4C5C]" /></div>
                <div>
                  <div className="font-semibold text-[#1F2937]">{test.test_name}</div>
                  <div className="text-sm text-[#6B7280]">{new Date(test.test_date).toLocaleDateString()}{test.description && ` | ${test.description}`}</div>
                  <div className="text-xs text-[#6B7280]">{test.filename}</div>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <a href={`${API}/medical-tests/file/${test.id}?token=${token}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-1.5 bg-[#0F4C5C] text-white rounded-lg text-sm hover:bg-[#0F4C5C]/90 transition-colors" data-testid={`download-test-${test.id}`}><Download className="w-3.5 h-3.5" /> {t.view}</a>
                <button onClick={() => handleDelete(test.id)} className="px-3 py-1.5 text-red-500 border border-red-200 rounded-lg text-sm hover:bg-red-50 transition-colors" data-testid={`delete-test-${test.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MedicalTests;
